/**
 * Role-source-of-truth helpers.
 *
 * Reality: a user's authority lives in TWO places that can both be
 * populated:
 *   1. `user.role`   — legacy singular field on the users collection.
 *                      Usually `branch_user` or `master_admin`; never
 *                      carries the new role codes (`TECHADMIN_VENDOR`
 *                      et al.) because the create-user flow leaves it
 *                      at the legacy default.
 *   2. `userRoles[]` — top-level array on the auth store, sourced from
 *                      the `user_roles` join table at login. This is
 *                      where new role codes live.
 *
 * Any access check that consults only one of those will silently lock
 * out legitimate users whose role lives in the other. The Jun-2026
 * DB-Explorer bug was three such single-source checks in a row. This
 * module centralises the union so every gate compares against the
 * SAME set, case-insensitively.
 */
import { useMemo } from 'react';
import useAuthStore from '../store/authStore';

/**
 * Build the upper-cased Set of roles a user holds, drawn from both
 * `user.role` (legacy) and `userRoles[]` (new). Empty / null entries
 * are dropped.
 *
 * Pure — accepts an auth-state-shaped object so it's testable without
 * mounting React.
 */
export function getActiveRoles({ user, userRoles } = {}) {
  const out = new Set();
  if (user && user.role) out.add(String(user.role).toUpperCase());
  if (Array.isArray(userRoles)) {
    for (const r of userRoles) {
      if (r) out.add(String(r).toUpperCase());
    }
  }
  return out;
}

/**
 * Pure boolean. Returns true iff the caller holds ANY role in the
 * provided allowlist. Case-insensitive.
 */
export function hasAnyRole(authState, allowedRoles) {
  if (!allowedRoles || !allowedRoles.length) return false;
  const mine = getActiveRoles(authState);
  if (!mine.size) return false;
  for (const r of allowedRoles) {
    if (mine.has(String(r).toUpperCase())) return true;
  }
  return false;
}

/**
 * React hook flavour. Subscribes to the auth store so the component
 * re-renders if the user's roles change at runtime (login, logout,
 * permissions refresh).
 */
export function useHasAnyRole(allowedRoles) {
  const user = useAuthStore((s) => s.user);
  const userRoles = useAuthStore((s) => s.userRoles);
  return useMemo(
    () => hasAnyRole({ user, userRoles }, allowedRoles),
    [user, userRoles, allowedRoles],
  );
}
